import { useEffect, useRef, useState } from 'react';
import {
  TextInput,
  required,
  useGetList,
  useTranslate,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import { PhoneInputWithCountry } from '../../../../../components';

interface Contact {
  id: number;
  uuid: string;
  name?: string | null;
  phoneNumber?: string | null;
}

interface ContactChoice {
  id: string;
  name: string;
  contactName: string | null;
  contactId?: number;
}

export interface ZapyTicketFormProps {
  channelConnectionId: number | null;
  onContactSelect: (choice: ContactChoice | null) => void;
  selectedContact: ContactChoice | null;
}

type SearchState = 'idle' | 'searching' | 'found' | 'not-found';

/**
 * Inner component that uses useWatch to observe the phoneNumber field value.
 * Must be rendered inside a form context (SimpleForm).
 */
const ZapyTicketFormContent = ({
  channelConnectionId,
  onContactSelect,
  selectedContact,
}: ZapyTicketFormProps) => {
  const translate = useTranslate();
  const phoneNumber = useWatch({ name: 'phoneNumber' });
  const contactName = useWatch({ name: 'contactName' });
  const prevPhoneRef = useRef<string | null>(null);
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const wasFetchingRef = useRef(false);

  // Only search when we have a valid E.164 phone (starts with + and at least 8 chars)
  const isValidPhone = !!phoneNumber && typeof phoneNumber === 'string'
    && phoneNumber.startsWith('+') && phoneNumber.length >= 8;

  // Search contacts by phone number
  const {
    data: contacts,
    isFetching,
  } = useGetList<Contact>('contact', {
    pagination: { page: 1, perPage: 5 },
    filter: { phoneNumber_q: phoneNumber },
    sort: { field: 'name', order: 'ASC' },
  }, { enabled: !!channelConnectionId && isValidPhone });

  // Clear selection and reset state when phone changes
  useEffect(() => {
    if (phoneNumber === prevPhoneRef.current) return;
    prevPhoneRef.current = phoneNumber;
    setSearchState('idle');
    wasFetchingRef.current = false;

    if (selectedContact) {
      onContactSelect(null);
    }
  }, [phoneNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track fetch transitions: idle → fetching → done
  useEffect(() => {
    if (isFetching) {
      wasFetchingRef.current = true;
      setSearchState('searching');
      return;
    }

    // isFetching just went false — query completed
    if (!wasFetchingRef.current) return;
    wasFetchingRef.current = false;

    if (!isValidPhone) {
      setSearchState('idle');
      return;
    }

    const exactMatch = contacts?.find(c => c.phoneNumber === phoneNumber);
    if (exactMatch) {
      setSearchState('found');
      onContactSelect({
        id: exactMatch.phoneNumber || phoneNumber,
        name: exactMatch.name
          ? `${exactMatch.name} (${exactMatch.phoneNumber})`
          : exactMatch.phoneNumber || phoneNumber,
        contactName: exactMatch.name || null,
        contactId: exactMatch.id,
      });
    } else {
      setSearchState('not-found');
      onContactSelect({
        id: phoneNumber,
        name: phoneNumber,
        contactName: null,
      });
    }
  }, [isFetching]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep contactName in sync for new contacts
  useEffect(() => {
    if (selectedContact?.contactId) return;
    if (searchState !== 'not-found') return;

    onContactSelect({
      id: phoneNumber,
      name: contactName ? `${contactName} (${phoneNumber})` : phoneNumber,
      contactName: contactName || null,
    });
  }, [contactName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!channelConnectionId) return null;

  return (
    <>
      <Box mb={2}>
        <Alert
          severity="info"
          icon={<Chip label={translate('resources.conversation.channelTypes.zapy')} color="success" size="small" />}
        >
          {translate('resources.conversation.channelTypes.zapyHelper')}
        </Alert>
      </Box>

      <PhoneInputWithCountry
        source="phoneNumber"
        label={translate('resources.conversation.fields.phoneNumber')}
        helperText={translate('resources.conversation.helpers.phoneNumber')}
        defaultCountry="BR"
        fullWidth
        validate={required()}
      />

      {/* Loading indicator while searching */}
      {searchState === 'searching' && (
        <Box mt={1} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {translate('resources.conversation.zapy.searching')}
          </Typography>
        </Box>
      )}

      {/* Contact found — show info */}
      {searchState === 'found' && selectedContact?.contactId && (
        <Box mt={2}>
          <Alert
            severity="success"
            icon={<PersonIcon />}
            action={
              <IconButton size="small" onClick={() => {
                onContactSelect(null);
                setSearchState('idle');
              }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2" fontWeight="medium">
              {selectedContact.contactName || selectedContact.id}
            </Typography>
            {selectedContact.contactName && (
              <Typography variant="caption" color="text.secondary">
                {selectedContact.id}
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      {/* No contact found — show name input for new contact */}
      {searchState === 'not-found' && (
        <Box mt={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {translate('resources.conversation.zapy.newContact')}
          </Alert>
          <TextInput
            source="contactName"
            label={translate('resources.contact.fields.name')}
            helperText={translate('resources.conversation.zapy.nameHelper')}
            fullWidth
          />
        </Box>
      )}
    </>
  );
};

/**
 * ZapyTicketForm - Phone number input with country selector for WhatsApp ticket creation
 *
 * Flow:
 * 1. User types phone number with PhoneInputWithCountry (BR default, auto DDI)
 * 2. System searches contacts automatically
 * 3. If exact match found → auto-selects and shows contact info
 * 4. If no match → shows name input for new contact creation
 */
export const ZapyTicketForm = (props: ZapyTicketFormProps) => {
  return <ZapyTicketFormContent {...props} />;
};

export default ZapyTicketForm;
